/**
 * Admin - Invite User Page
 *
 * Allows admins to send invitation links with role selection
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../platform/api';
import { Mail, ArrowLeft, Copy, Check, User, Shield, Eye } from 'lucide-react';

interface InvitationResponse {
  data: {
    id: string;
    email: string;
    code: string;
    status: string;
    expires_at: string;
    role: string;
  };
  invitation_link: string;
}

export function AdminInviteUserPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'user' | 'admin' | 'viewer'>('user');
  const [expiresInDays, setExpiresInDays] = useState(7);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const roles = [
    {
      value: 'user' as const,
      label: 'User',
      description: 'Can create content and manage their own data',
      icon: User,
      color: 'blue',
    },
    {
      value: 'admin' as const,
      label: 'Admin',
      description: 'Full access to all features and user management',
      icon: Shield,
      color: 'purple',
    },
    {
      value: 'viewer' as const,
      label: 'Viewer',
      description: 'Read-only access for monitoring and reviewing',
      icon: Eye,
      color: 'gray',
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await apiRequest<InvitationResponse>('/api/admin/invitations', {
        method: 'POST',
        body: {
          email,
          name: name || undefined,
          role,
          expires_in_days: expiresInDays,
        },
      });

      setInvitation(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (invitation) {
      await navigator.clipboard.writeText(invitation.invitation_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendAnother = () => {
    setInvitation(null);
    setEmail('');
    setName('');
    setRole('user');
    setCopied(false);
  };

  if (invitation) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Invitation Sent!</h2>
              <p className="text-gray-600 mt-2">
                An invitation has been created for{' '}
                <span className="font-medium">{invitation.data.email}</span>
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invitation Link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={invitation.invitation_link}
                  readOnly
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-mono"
                />
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Expires on{' '}
                {new Date(invitation.data.expires_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Role:</span>
                <span className="font-medium text-gray-900 capitalize">
                  {invitation.data.role}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Status:</span>
                <span className="font-medium text-gray-900 capitalize">
                  {invitation.data.status}
                </span>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={handleSendAnother}
                className="flex-1 px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 transition-colors"
              >
                Send Another Invitation
              </button>
              <button
                onClick={() => navigate('/admin/invitations')}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                View All Invitations
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Admin
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-mazo-100 rounded-lg">
              <Mail className="h-6 w-6 text-mazo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Invite User</h1>
              <p className="text-sm text-gray-500 mt-1">
                Send an invitation link to onboard a new user
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="user@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
              />
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name (Optional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
              />
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Role <span className="text-red-500">*</span>
              </label>
              <div className="grid gap-3">
                {roles.map((roleOption) => {
                  const Icon = roleOption.icon;
                  const isSelected = role === roleOption.value;
                  return (
                    <button
                      key={roleOption.value}
                      type="button"
                      onClick={() => setRole(roleOption.value)}
                      className={`
                        p-4 rounded-lg border-2 text-left transition-all
                        ${
                          isSelected
                            ? 'border-mazo-500 bg-mazo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`
                          p-2 rounded-lg
                          ${isSelected ? 'bg-mazo-100' : 'bg-gray-100'}
                        `}
                        >
                          <Icon
                            className={`h-5 w-5 ${isSelected ? 'text-mazo-600' : 'text-gray-600'}`}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {roleOption.label}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {roleOption.description}
                          </div>
                        </div>
                        {isSelected && (
                          <Check className="h-5 w-5 text-mazo-600 mt-1" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link Expires In
              </label>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
              >
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !email}
              className="w-full px-4 py-3 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {loading ? 'Creating Invitation...' : 'Create Invitation'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminInviteUserPage;
